/**
 * Meeting Tools sections — ported from Figma meetingToolSections.
 *
 * Flow:
 *   step 1) getMeetingToolSections(role) → Learning / Interaction / …
 *   step 2) MeetingToolsRail renders buttons
 *   step 3) Parent opens tool panel or existing room action
 *
 * Note: Mic & Video lives inside Participants (no separate tool).
 */

import React from "react";
import {
  BarChart3,
  BookOpen,
  CircleHelp,
  EyeOff,
  HelpCircle,
  ListChecks,
  ListOrdered,
  MessageSquare,
  Radio,
  Settings,
  Users,
} from "lucide-react";

export type LiveSessionRole = "student" | "trainer";

export type MeetingToolId =
  | "quiz"
  | "resources"
  | "progress"
  | "qa"
  | "announcements"
  | "polls"
  | "feedback"
  | "chat"
  | "participants"
  | "engagement"
  | "distraction"
  | "absorption"
  | "quiz-scores"
  | "session-topics"
  | "manage-topic"
  | "timer"
  | "recording"
  | "captions"
  | "network"
  | "settings";

export type MeetingToolItem = {
  id: MeetingToolId;
  label: string;
  icon: React.ReactNode;
};

export type MeetingToolSection = {
  title: string;
  tools: MeetingToolItem[];
};

const icon = (node: React.ReactNode) => node;

export function getMeetingToolSections(
  role: LiveSessionRole,
  options?: { canManageTopics?: boolean },
): MeetingToolSection[] {
  // ---------------------------------------------------------------
  // step 1: Student — Learning + Interaction only (screenshot set)
  // ---------------------------------------------------------------
  if (role === "student") {
    return [
      {
        title: "Learning",
        tools: [
          { id: "quiz", label: "Quizzes", icon: icon(<ListChecks size={16} />) },
          {
            id: "resources",
            label: "Resources",
            icon: icon(<BookOpen size={16} />),
          },
          {
            id: "progress",
            label: "Progress",
            icon: icon(<BarChart3 size={16} />),
          },
        ],
      },
      {
        title: "Interaction",
        tools: [
          { id: "qa", label: "Q&A", icon: icon(<HelpCircle size={16} />) },
          {
            id: "announcements",
            label: "Announcements",
            icon: icon(<Radio size={16} />),
          },
          { id: "polls", label: "Polls", icon: icon(<BarChart3 size={16} />) },
          {
            id: "feedback",
            label: "Feedback",
            icon: icon(<MessageSquare size={16} />),
          },
          { id: "chat", label: "Chat", icon: icon(<MessageSquare size={16} />) },
        ],
      },
    ];
  }

  // ---------------------------------------------------------------
  // step 2: Trainer — Interaction + Analytics + Session only
  // Co-trainers: no topic management or recording tools.
  // ---------------------------------------------------------------
  const canManageTopics = options?.canManageTopics === true;
  const sessionTools: MeetingToolItem[] = [];
  if (canManageTopics) {
    sessionTools.push(
      {
        id: "session-topics",
        label: "Session Topics",
        icon: icon(<ListOrdered size={16} />),
      },
      {
        id: "manage-topic",
        label: "Start / Manage Topic",
        icon: icon(<CircleHelp size={16} />),
      },
      {
        id: "recording",
        label: "Recording",
        icon: icon(<Radio size={16} />),
      },
    );
  }
  sessionTools.push({
    id: "settings",
    label: "Settings",
    icon: icon(<Settings size={16} />),
  });

  return [
    {
      title: "Interaction",
      tools: [
        {
          id: "participants",
          label: "Participants",
          icon: icon(<Users size={16} />),
        },
        {
          id: "qa",
          label: "Student Q&A",
          icon: icon(<HelpCircle size={16} />),
        },
        {
          id: "announcements",
          label: "Announcements",
          icon: icon(<Radio size={16} />),
        },
        { id: "polls", label: "Polls", icon: icon(<BarChart3 size={16} />) },
        {
          id: "chat",
          label: "Class Chat",
          icon: icon(<MessageSquare size={16} />),
        },
      ],
    },
    {
      title: "Analytics",
      tools: [
        {
          id: "engagement",
          label: "Attendance & Engagement",
          icon: icon(<Users size={16} />),
        },
        {
          id: "distraction",
          label: "Distraction Report",
          icon: icon(<EyeOff size={16} />),
        },
        {
          id: "absorption",
          label: "Topic Absorption",
          icon: icon(<CircleHelp size={16} />),
        },
        {
          id: "quiz-scores",
          label: "Quiz Scores",
          icon: icon(<BarChart3 size={16} />),
        },
      ],
    },
    {
      title: "Session",
      tools: sessionTools,
    },
  ];
}
